import React from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { ff } from 'helpers/featureFlags';
import { AttributeAnchors } from 'components/Anchor/Anchor';
import DebugRender from 'components/Debug/Render';
import classnames from 'classnames';
import { ariaLabel } from '../Generic';
import { stateFormErrorConnector } from '../shared/stateFormErrors';
import ValidationElement from '../ValidationElement';


class SelectDropdown extends ValidationElement {
  constructor(props) {
    super(props);

    this.state = {
      uid: `${props.name}-${super.guid()}`,
      hasErrors: false,
      isValid: false,
      // EN-5056 adding value to state to support blanking out value when component is set to disabled
      // this functionality only occurs in daterange component when Present is checked
      value: props.value,
    };
    this.checkComponentValidityAndErrors = this.checkComponentValidityAndErrors.bind(this);
    this.errors = this.errors.bind(this);
    this.selectRef = React.createRef();
  }

  componentDidMount() {
    const { value } = this.props;
    this.checkComponentValidityAndErrors(value);
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (prevProps.value !== value) {
      this.checkComponentValidityAndErrors(value);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    let newState = {};
    if (!isEqual(nextProps.value, this.props.value) || nextProps.isDisabled !== this.props.isDisabled) {
      if (nextProps.value !== this.state.value) {
        newState = { ...newState, value: nextProps.value };
      }

      // If disabled, we clear the value and clear state
      if (nextProps.isDisabled) {
        newState = { value: '', isValid: null, hasErrors: null };
      }

      if (newState.value !== undefined && newState.value !== this.state.value) {
        const errors = this.errors(newState.value, nextProps);
        newState.hasError = errors.some((x) => x.isValid === false);
        newState.isValid = errors.every((x) => x.isValid === true);
      }

      this.setState(newState);
    }
  }

  /**
   * This allows us to switch logic of "hasError" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  hasError = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return this.props.hasStateFormError;
    }
    return this.state.hasErrors;
  };

  /**
   * This allows us to switch logic of "isValid" between state.application and
   * state.form. When useStateFormErrors is true, we'll use
   * this.props.hasStateFormError. Otherwise, continue to use the previous error
   * handling.
   */
  isValid = () => {
    if (ff(this.props.features).getActive('EN-4733')) {
      return !this.props.hasStateFormError && this.props.value.length > 0;
    }
    return this.state.isValid;
  };

  checkComponentValidityAndErrors(value) {
    const errors = this.errors(value);
    this.setState({
      hasErrors: errors.some((err) => err.valid === false),
      isValid: errors.every((err) => err.valid === true),
      value: this.props.value,
    });
  }

  errors(value, props = this.props) {
    return this.props.onError(
      value,
      this.constructor.errors.map((err) => ({
        code: err.code,
        valid: err.func(value, props),
        uid: this.state.uid,
      }))
    );
  }

  render() {
    const {
      children, isDisabled, name, onChange
    } = this.props;
    let speech = '';

    if (this.props.ariaLabel) {
      speech = this.props.ariaLabel;
    } else if (this.props.label && this.selectRef?.current?.ariaLabel) {
      speech = `${this.props.ariaPrepend} ${this.props.label} for ${ariaLabel(this.selectRef.current)}`;
    } else {
      speech = `${this.props.ariaPrepend} ${this.props.label}`;
    }

    return (
      <div className={classnames(this.props.className, { 'usa-input-error': this.hasError() })}>
        <AttributeAnchors attribute={this.props.attribute} />
        {this.props.label && (
          <label
            className={classnames(
              { disabled: this.props.isDisabled },
              { 'usa-input-error-label': this.hasError() }
            )}
            htmlFor={this.props.id ?? this.state.uid}
            aria-label={speech}
          >

            {this.props.label}
          </label>
        )}
        <DebugRender parent={this} />
        <select
          className={classnames(
            { 'usa-input-success': this.isValid() && !isDisabled },
            { disabled: this.props.isDisabled }
          )}
          name={name}
          ref={this.selectRef}
          onChange={onChange}
          value={this.state.value}
          disabled={isDisabled}
          aria-label={speech}
          id={this.props.id ?? this.state.uid}
        >
          {children}
        </select>
      </div>
    );
  }
}

SelectDropdown.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

SelectDropdown.defaultProps = {
  className: '',
  ariaPrepend: '',
  label: '',
  isDisabled: false,
  isRequired: false,
};

SelectDropdown.errors = [
  {
    code: 'required',
    func: (value, props) => {
      if (props.isRequired) {
        return !!value;
      }
      return value ? true : null;
    },
  },
];

export const internalTestingSelectDropdown = {
  SelectDropdown,
};
export default stateFormErrorConnector(SelectDropdown);
